<template lang="pug">
y-dialog(
  fill-height
  fluid
  v-model="showModal"
  scrollable
  transition="fade-transition"
  persistent
)
  v-card
    v-card-title.px-4.h6.basic800--text {{ $t('create_invoice') }}
    v-divider.basic300
    v-card-text.pb-0.px-4.pt-4
      v-form(ref="form")
        // Error Alert
        v-alert(
          v-if="error"
          type="error"
          text
          dismissible
          @input="error = null"
        ) {{ error }}

        // Issuer
        p.mb-0.label.basic600--text.text-uppercase {{$t('issued_by')}}
        v-text-field.mt-2(
          v-model="invoiceData.issuer"
          background-color="basic200" height="40" outlined dense persistent-hint
          :rules="rules.not_empty"
        )

        // Due Date
        p.mb-0.label.basic600--text.text-uppercase Due Date
        v-text-field.mt-2(
          v-model="invoiceData.due_date"
          background-color="basic200" height="40" outlined dense persistent-hint
          :rules="rules.not_empty"
        )

        // Client Details
        .h6.basic800--text.mb-2 {{ $t('company_details') }}
        p.mb-0.label.basic600--text.text-uppercase {{$t('company_name')}}
        v-text-field.mt-2(
          v-model="invoiceData.client.company_name"
          :rules="rules.not_empty"
          background-color="basic200" height="40" outlined dense persistent-hint
        )

        p.mb-0.label.basic600--text.text-uppercase {{$t('clinic_name')}}
        v-text-field.mt-2(
          v-model="invoiceData.client.clinic_name"
          :rules="rules.not_empty"
          background-color="basic200" height="40" outlined dense persistent-hint
        )
        p.mb-0.label.basic600--text.text-uppercase TIN Number
        v-text-field.mt-2(
          v-model="invoiceData.client.tin_number"
          background-color="basic200" height="40" outlined dense persistent-hint
        )

        p.mb-0.label.basic600--text.text-uppercase {{$t('company_address')}}
        v-text-field.mt-2(
          v-model="invoiceData.client.address"
          :rules="rules.not_empty"
          background-color="basic200" height="40" outlined dense persistent-hint
        )
        p.mb-0.label.basic600--text.text-uppercase {{$t('company_email')}}
        v-text-field.mt-2(
          v-model="invoiceData.client.email"
          :rules="rules.not_empty"
          background-color="basic200" height="40" outlined dense persistent-hint
        )

        // Products
        .h6.basic800--text.mb-2 {{ $t('products') }}
        .mb-4(v-for="(product, index) in invoiceData.products" :key="index")
          v-card(outlined)
            v-card-text
              p.mb-0.label.basic600--text.text-uppercase {{$t('product_name')}}
              v-text-field.mt-2(
                v-model="product.name"
                :rules="rules.not_empty"
                background-color="basic200" height="40" outlined dense persistent-hint
              )
              p.mb-0.label.basic600--text.text-uppercase {{$t('contract')}}
              v-text-field.mt-2(
                v-model="product.contract"
                :rules="rules.not_empty"
                background-color="basic200" height="40" outlined dense persistent-hint
              )
              p.mb-0.label.basic600--text.text-uppercase {{$t('value')}}
              v-text-field.mt-2(
                v-model="product.value"
                type="number"
                :rules="rules.not_empty"
                background-color="basic200" height="40" outlined dense persistent-hint
              )
              v-btn(@click="removeProduct(index)" color="error" outlined text small)
                | {{ $t('remove') }}
        v-btn(@click="addProduct" color="primary" text outlined small)
          | {{ $t('add_product') }}

        // Discounts
        .h6.basic800--text.mb-2.mt-4 {{ $t('discounts') }}
        .mb-4(v-for="(discount, index) in invoiceData.discounts" :key="index")
          v-card(outlined)
            v-card-text
              p.mb-0.label.basic600--text.text-uppercase {{$t('discount_description')}}
              v-text-field.mt-2(
                v-model="discount.description"
                :rules="rules.not_empty"
                background-color="basic200" height="40" outlined dense persistent-hint
              )
              p.mb-0.label.basic600--text.text-uppercase {{$t('amount')}}
              v-text-field.mt-2(
                v-model="discount.amount"
                type="number"
                :rules="rules.not_empty"
                background-color="basic200" height="40" outlined dense persistent-hint
              )
              v-btn(@click="removeDiscount(index)" color="error" text small outlined)
                | {{ $t('remove') }}
        v-btn(@click="addDiscount" color="primary" text outlined small)
          | {{ $t('add_discount') }}

        // Total Amount
        .h6.basic800--text.mb-2.mt-4 {{ $t('total') }}
        v-text-field(
          :value="totalAmount.toFixed(2)"
          type="number"
          readonly
          background-color="basic200" height="40" outlined dense persistent-hint
        )

    v-card-actions
      v-spacer
      v-btn(color='white', text @click='showModal = false')
        .button-Aa-small.primary400--text {{ $t('cancel') }}
      v-btn.mr-2(
        color='textColorAccent'
        depressed
        height=32
        @click='clickUpdate'
        :loading="isUpdating"
        :disabled="!canSubmit"
        dark
      )
        .button-AA-small.basic100--text {{ $t('update') }}
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'CreateInvoice',
  props: ['open', 'lead'],
  data() {
    return {
      invoiceData: {
        issuer: '',
        client: {
          company_name: '',
          clinic_name: '',
          address: '',
          email: ''
        },
        products: [{
          name: '',
          contract: '',
          value: 0
        }],
        discounts: [],
        total: {
          amount: 0
        }
      },
      error: null,
      isUpdating: false,
      rules: {
        not_empty: [val => (val || '').toString().length > 0 || 'This field is required']
      }
    }
  },
  computed: {
    showModal: {
      get() {
        return this.open;
      },
      set(newValue) {
        this.$emit('update:open', newValue);
      }
    },
    canSubmit() {
      return this.validateForm() && this.$refs.form.validate();
    },
    totalProductValue() {
      return this.invoiceData.products.reduce((sum, product) => 
        sum + Number(product.value), 0);
    },
    totalDiscounts() {
      return this.invoiceData.discounts.reduce((sum, discount) => 
        sum + Number(discount.amount), 0);
    },
    totalAmount() {
      return this.totalProductValue - this.totalDiscounts;
    }
  },
  watch: {
    totalAmount(newValue) {
      this.invoiceData.total.amount = newValue; // Update total amount in invoiceData
    }
  },
  mounted() {
    this.initializeData();
  },
  methods: {
    ...mapActions(['showMessage']),
    validateForm() {
      return (
        this.invoiceData.issuer &&
        this.invoiceData.client.company_name &&
        this.invoiceData.products.length > 0
      );
    },
    addProduct() {
      this.invoiceData.products.push({
        name: '',
        contract: '',
        value: 0
      });
    },
    removeProduct(index) {
      this.invoiceData.products.splice(index, 1);
    },
    addDiscount() {
      this.invoiceData.discounts.push({
        description: '',
        amount: 0
      });
    },
    removeDiscount(index) {
      this.invoiceData.discounts.splice(index, 1);
    },
    initializeData() {
      if (this.lead.extra_fields_data) {
        const invoiceField = this.lead.extra_fields_data.find(field => 
          field.name === 'Invoice' && field.enabled
        );
        if (invoiceField && invoiceField.value) {
          try {
            this.invoiceData = JSON.parse(invoiceField.value);
          } catch (e) {
            this.error = this.$t('error_message.invalid_invoice_format');
          }
        }
      }
    },
    clickUpdate() {
      if (!this.$refs.form.validate()) return;

      this.isUpdating = true;

      const extraField = {
        type: 'json',
        name: 'Invoice',
        enabled: true,
        value: JSON.stringify(this.invoiceData),
        required: true
      };

      const extra_fields_data = Array.isArray(this.lead?.extra_fields_data) 
        ? [...this.lead.extra_fields_data]
        : [];

      const existingIndex = extra_fields_data.findIndex(field => field.name === 'Invoice');
      
      if (existingIndex >= 0) {
        extra_fields_data[existingIndex] = { ...extra_fields_data[existingIndex], ...extraField };
      } else {
        extra_fields_data.push(extraField);
      }

      this.$axios.patch(`/leads/${this.lead.pid}/`, { extra_fields_data })
        .then(resp => {
          this.$emit('update:lead', resp.data);
          this.showModal = false;
          this.showMessage(['success', this.$t('success_message.invoice_updated')]);
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')]);
        })
        .finally(() => {
          this.isUpdating = false;
        });
    }
  }
}
</script>

<i18n>
{
  "en": {
    "create_invoice": "Create Invoice",
    "issued_by": "Issued By",
    "company_details": "Company Details", 
    "company_name": "Company Name",
    "clinic_name": "Clinic Name",
    "company_address": "Company Address",
    "company_email": "Company Email",
    "products": "Products",
    "product_name": "Product Name",
    "contract": "Contract",
    "value": "Value",
    "discounts": "Discounts",
    "discount_description": "Description",
    "amount": "Amount",
    "total": "Total",
    "total_amount": "Total Amount",
    "includes_sst": "Includes SST",
    "add_product": "Add Product",
    "add_discount": "Add Discount",
    "remove": "Remove",
    "cancel": "Cancel",
    "update": "Update",
    "error_message": {
      "invalid_invoice_format": "Invalid invoice format",
      "unable_to_complete_request": "Unable to update invoice. Please try again."
    },
    "success_message": {
      "invoice_updated": "Invoice updated successfully"
    }
  }
}
</i18n>